import dayjs, { Dayjs } from 'dayjs';
import { KiloClassAthleteAgenda, KiloClassAthleteRetrieve } from 'types';

import { useClassTiming } from './useClassTiming';

export type ClassMeta = {
  classId: string;
  eventStartDateTime: Dayjs;
  hasStarted: boolean;
  isWithinReservationWindow: boolean;
  isWithinCancellationWindow: boolean;
  isWithinLateCancellationWindow: boolean;
  capacity: number;
  inClassCount: number;
  isAtCapacity: boolean;
  isWaitlistAtCapacity: boolean;
  classSizeDisplay: string;
};

export function useClassMeta(
  classData: KiloClassAthleteAgenda | KiloClassAthleteRetrieve | undefined,
): ClassMeta {
  const {
    eventStartDateTime,
    hasStarted,
    isWithinReservationWindow,
    isWithinCancellationWindow,
    isWithinLateCancellationWindow,
  } = useClassTiming(classData);

  if (!classData) {
    return {
      classId: '',
      eventStartDateTime: dayjs(),
      hasStarted: false,
      isWithinReservationWindow: false,
      isWithinCancellationWindow: false,
      isWithinLateCancellationWindow: false,
      capacity: 0,
      inClassCount: 0,
      isAtCapacity: false,
      isWaitlistAtCapacity: false,
      classSizeDisplay: '',
    };
  }

  const inClassCount = classData.athleteKiloClasses.filter(
    ({ isReserved, isCheckedIn, isNoShow, isLateCancel }) =>
      (isReserved || isCheckedIn) && !isNoShow && !isLateCancel,
  ).length;

  const waitlistCount = classData.athleteKiloClasses.filter(({ isWaitlisted }) => isWaitlisted).length;
  const isAtCapacity = classData.capacity - inClassCount <= 0;
  const isWaitlistAtCapacity = classData.waitlistLimit ? classData.waitlistLimit - waitlistCount <= 0 : true;

  const classSizeDisplay = `${inClassCount} / ${classData.capacity}`;

  return {
    classId: classData.kiloClassId,
    eventStartDateTime,
    hasStarted,
    isWithinReservationWindow,
    isWithinCancellationWindow,
    isWithinLateCancellationWindow,
    capacity: classData.capacity,
    inClassCount,
    isAtCapacity,
    isWaitlistAtCapacity,
    classSizeDisplay,
  };
}
